/**
 * sidebarViewFilters.ts — pure filter/group transforms for the sidebar
 * workspace list options menu.
 *
 * The menu mirrors the shape users know from Claude's sidebar (Status,
 * Environment, Show empty…, Group by, Sort by) but binds each control to a
 * concept Forge actually has. Two deliberate translations:
 *
 *   • Claude groups by *folder*. Forge has no folder taxonomy — projects are
 *     identified by environment (cloud / desktop) — so "Group by" offers
 *     Environment, which is the real axis a Forge user sorts along.
 *   • Claude's "Show empty folders" becomes "Show empty projects": a project
 *     with no threads left after the Status filter applies.
 *
 * Every function here is a pure transform. No React, matching the sibling
 * `*.logic.ts` modules in this directory.
 */

import type { ProjectGroup, ProjectEnvironmentKind } from "./projectGroupedSidebar.logic";

// ---------------------------------------------------------------------------
// Vocabularies
// ---------------------------------------------------------------------------

export const SIDEBAR_STATUS_FILTERS = ["active", "archived", "all"] as const;
export type SidebarStatusFilter = (typeof SIDEBAR_STATUS_FILTERS)[number];

export const SIDEBAR_ENVIRONMENT_FILTERS = ["all", "cloud", "desktop"] as const;
export type SidebarEnvironmentFilter = (typeof SIDEBAR_ENVIRONMENT_FILTERS)[number];

export const SIDEBAR_GROUP_BY_OPTIONS = ["none", "environment"] as const;
export type SidebarGroupBy = (typeof SIDEBAR_GROUP_BY_OPTIONS)[number];

export interface SidebarViewFilters {
  readonly status: SidebarStatusFilter;
  readonly environment: SidebarEnvironmentFilter;
  readonly groupBy: SidebarGroupBy;
  readonly showEmptyWorkspaces: boolean;
}

/**
 * `showEmptyWorkspaces` defaults to true because the sidebar has always shown
 * every workspace regardless of thread count. Defaulting it off would silently
 * hide workspaces users can see today.
 */
export const DEFAULT_SIDEBAR_VIEW_FILTERS: SidebarViewFilters = {
  status: "active",
  environment: "all",
  groupBy: "none",
  showEmptyWorkspaces: true,
};

export const SIDEBAR_STATUS_FILTER_LABEL: Record<SidebarStatusFilter, string> = {
  active: "Active",
  archived: "Archived",
  all: "All",
};

export const SIDEBAR_ENVIRONMENT_FILTER_LABEL: Record<SidebarEnvironmentFilter, string> = {
  all: "All",
  cloud: "Cloud",
  desktop: "Desktop",
};

export const SIDEBAR_GROUP_BY_LABEL: Record<SidebarGroupBy, string> = {
  none: "None",
  environment: "Environment",
};

// ---------------------------------------------------------------------------
// Predicates
// ---------------------------------------------------------------------------

export interface StatusFilterThreadShape {
  readonly archivedAt?: string | null;
}

/**
 * Undefined `archivedAt` counts as active: older snapshots omit the field, and
 * treating them as archived would blank the sidebar.
 */
export function matchesStatusFilter(
  thread: StatusFilterThreadShape,
  status: SidebarStatusFilter,
): boolean {
  if (status === "all") return true;
  const isArchived = thread.archivedAt != null;
  return status === "archived" ? isArchived : !isArchived;
}

/**
 * A `mixed` workspace has copies on more than one environment, so it matches
 * both the cloud and the desktop filter rather than falling through both.
 */
export function matchesEnvironmentFilter(
  environmentKind: ProjectEnvironmentKind,
  environment: SidebarEnvironmentFilter,
): boolean {
  if (environment === "all") return true;
  if (environmentKind === "mixed") return true;
  return environmentKind === environment;
}

// ---------------------------------------------------------------------------
// Group transforms
// ---------------------------------------------------------------------------

/**
 * Applies Status, Environment, and Show-empty in the one order that reads
 * correctly: thread-level status first (it decides which workspaces are empty),
 * then workspace-level environment, then the empty-workspace rule.
 */
export function applySidebarViewFilters(
  groups: readonly ProjectGroup[],
  filters: SidebarViewFilters,
): readonly ProjectGroup[] {
  const withFilteredThreads = groups.map((group) => ({
    ...group,
    threads: group.threads.filter((row) => matchesStatusFilter(row.thread, filters.status)),
  }));

  return withFilteredThreads.filter((group) => {
    if (!matchesEnvironmentFilter(group.environmentKind, filters.environment)) return false;
    if (filters.showEmptyWorkspaces) return true;
    return group.threads.length > 0;
  });
}

export interface SidebarGroupSection {
  readonly key: string;
  /** Null when the list is ungrouped — the caller renders no header. */
  readonly label: string | null;
  readonly groups: readonly ProjectGroup[];
}

const ENVIRONMENT_SECTION_ORDER: readonly ProjectEnvironmentKind[] = ["cloud", "desktop", "mixed"];

const ENVIRONMENT_SECTION_LABEL: Record<ProjectEnvironmentKind, string> = {
  cloud: "Cloud",
  desktop: "Desktop",
  mixed: "Multiple environments",
};

/**
 * Splits the list into rendered sections. `none` yields a single unlabelled
 * section so the renderer has one code path for both modes.
 */
export function buildSidebarGroupSections(
  groups: readonly ProjectGroup[],
  groupBy: SidebarGroupBy,
): readonly SidebarGroupSection[] {
  if (groupBy === "none") {
    return [{ key: "all", label: null, groups }];
  }

  return ENVIRONMENT_SECTION_ORDER.flatMap((kind) => {
    const matching = groups.filter((group) => group.environmentKind === kind);
    if (matching.length === 0) return [];
    return [{ key: kind, label: ENVIRONMENT_SECTION_LABEL[kind], groups: matching }];
  });
}

/**
 * Manual drag ordering is only coherent in a flat list: once the list is split
 * into environment sections, a drop position no longer maps to a single global
 * order. The renderer uses this to disable drag rather than let a drop silently
 * do nothing.
 */
export function isManualOrderingAvailable(groupBy: SidebarGroupBy): boolean {
  return groupBy === "none";
}
